// Team Management settings strings — EN + TR.
//
// Wave-2 Day 2. Settings sub-section for team CRUD (per-surface; admin Ops
// Team / seller My Team / supply My Team). 593 LOC source file was the worst
// MIXED offender per i18n audit 2026-06-02 (commit ca624ba):
// - time-ago strings TR ("dk önce / saat önce / gün önce") inline helper
// - headers EN ("Ops Team / My Team / Add Member")
// - role permission descriptions EN (live in mocks/data/settings.ts)
//
// Time-ago migration: TeamManagement adopts Wave-1 `lib/datetime.formatRelative`
// helper instead of inline `timeAgo()` function — DRY consolidation (raw
// "dk önce" strings no longer duplicated here).
//
// Role label + summary override: mock data exposes EN-only `label` + `summary`
// (Stage 4 SPEC.md Faz 1 ratifies the permission semantics, not the i18n
// payload). Until Faz 1 ships locale-aware role descriptors, the dictionary
// owns the bilingual wording via `roles[surface][value]` lookup; mock data
// values still resolve role identity (foreign key for backend).
//
// Source: features/settings/TeamManagement.tsx.

import type { LocalizedDict } from '../index'

type RoleStrings = {
  label: string
  summary: string
}

type SurfaceHeader = {
  title: string
  subtitle: string
}

type TeamManagementStrings = {
  header: {
    admin: SurfaceHeader
    seller: SurfaceHeader
    supply: SurfaceHeader
  }
  addMember: string
  footerNote: string
  columns: {
    avatar: string
    name: string
    email: string
    role: string
    status: string
    lastActive: string
    actions: string
  }
  status: {
    active: string
    invited: string
    inactive: string
  }
  invitedPrefix: string
  noLastActive: string
  states: {
    loading: string
    loadFailed: string
    empty: string
  }
  ariaLabels: {
    editMember: (name: string) => string
    removeMember: (name: string) => string
    cannotRemove: string
  }
  modal: {
    editTitle: string
    addTitle: string
    descriptionEdit: string
    descriptionAdd: string
    displayName: string
    displayNamePlaceholder: string
    email: string
    emailPlaceholder: string
    emailLocked: string
    role: string
    statusLabel: string
    statusActive: string
    statusInvited: string
    statusInactive: string
    sendInviteLabel: string
    cancel: string
    saving: string
    update: string
    sendInvite: string
    validation: {
      required: string
      invalidEmail: string
    }
  }
  deleteDialog: {
    title: string
    leadBefore: string
    leadAfter: string
    cancel: string
    confirm: string
    forceConfirm: string
    removing: string
  }
  rolesMatrix: {
    title: string
    footer: string
  }
  roles: {
    admin: {
      admin: RoleStrings
      ops: RoleStrings
      customer_support: RoleStrings
      graphic_designer: RoleStrings
      shipping: RoleStrings
      warehouse_lead: RoleStrings
    }
    seller: {
      owner: RoleStrings
      manager: RoleStrings
      assistant: RoleStrings
      designer: RoleStrings
    }
    supply: {
      owner: RoleStrings
      production_lead: RoleStrings
      helper: RoleStrings
    }
  }
}

export const teamManagementI18n: LocalizedDict<TeamManagementStrings> = {
  en: {
    header: {
      admin: {
        title: 'Ops Team',
        subtitle:
          'Ops team members can be assigned to orders in Order Management.',
      },
      seller: {
        title: 'My Team',
        subtitle:
          'Add team members to your seller account. They will appear as Assign To options in My Orders.',
      },
      supply: {
        title: 'My Team',
        subtitle:
          'Production crew members can be assigned to work orders. Owner + production lead role separation honored.',
      },
    },
    addMember: 'Add Member',
    footerNote:
      'Team members of this workspace.',
    columns: {
      avatar: 'Avatar',
      name: 'Name',
      email: 'Email',
      role: 'Role',
      status: 'Status',
      lastActive: 'Last active',
      actions: 'Actions',
    },
    status: {
      active: 'Active',
      invited: 'Invited',
      inactive: 'Inactive',
    },
    invitedPrefix: 'Invited',
    noLastActive: '—',
    states: {
      loading: 'Loading team…',
      loadFailed:
        'Could not load the team. Try again.',
      empty: 'No team members yet. Use "Add Member" to get started.',
    },
    ariaLabels: {
      editMember: (name) => `Edit ${name}`,
      removeMember: (name) => `Remove ${name}`,
      cannotRemove: "Owner/admin can't be removed",
    },
    modal: {
      editTitle: 'Edit Team Member',
      addTitle: 'Add Team Member',
      descriptionEdit:
        'Update display name, role and status.',
      descriptionAdd:
        'Add a team member.',
      displayName: 'Display Name',
      displayNamePlaceholder: 'e.g. Maya Patel',
      email: 'Email',
      emailPlaceholder: 'user@example.com',
      emailLocked:
        'Email cannot be edited.',
      role: 'Role',
      statusLabel: 'Status',
      statusActive: 'Active',
      statusInvited: 'Invited',
      statusInactive: 'Inactive',
      sendInviteLabel: 'Mark as invited',
      cancel: 'Cancel',
      saving: 'Saving…',
      update: 'Update',
      sendInvite: 'Send invite',
      validation: {
        required: 'Display name, email and role are required.',
        invalidEmail: 'A valid email address is required.',
      },
    },
    deleteDialog: {
      title: 'Remove team member?',
      leadBefore: '',
      leadAfter:
        ' will be removed from the team.',
      cancel: 'Cancel',
      confirm: 'Remove',
      forceConfirm: 'Remove anyway (force)',
      removing: 'Removing…',
    },
    rolesMatrix: {
      title: 'Roles — what each role can do (read-only preview)',
      footer:
        'Roles describe what each team member can do.',
    },
    roles: {
      admin: {
        admin: {
          label: 'Admin',
          summary:
            'Full platform governance · Ring 3 audit access · approve payouts',
        },
        ops: {
          label: 'Ops',
          summary:
            'Daily order workflow · assign + tag · address verification review',
        },
        customer_support: {
          label: 'Customer Support',
          summary:
            'Read orders + customer comms · escalate replacements + holds',
        },
        graphic_designer: {
          label: 'Graphic Designer',
          summary:
            'Personalization queue · proof generation · design asset library',
        },
        shipping: {
          label: 'Shipping',
          summary: 'Label batch print · carrier select · pickup scheduling',
        },
        warehouse_lead: {
          label: 'Warehouse Lead',
          summary:
            'Inventory pool · receiving + putaway · daily outbound batch lead',
        },
      },
      seller: {
        owner: {
          label: 'Owner',
          summary:
            'Account owner · billing + team config · full operational scope',
        },
        manager: {
          label: 'Manager',
          summary:
            'Daily orders + inventory + report submission · cannot manage team',
        },
        assistant: {
          label: 'Assistant',
          summary:
            'Order assignment recipient · limited edit · view-only billing',
        },
        designer: {
          label: 'Designer',
          summary:
            'Personalization fulfillment · proof handoff · no order finance',
        },
      },
      supply: {
        owner: {
          label: 'Owner',
          summary:
            'Workshop owner · WO acceptance + invoice · full team config',
        },
        production_lead: {
          label: 'Production Lead',
          summary:
            'Daily WO production scheduling · QC sign-off · shipment handoff',
        },
        helper: {
          label: 'Helper',
          summary:
            'Assigned tasks only · packaging + finishing · no WO state change',
        },
      },
    },
  },
  tr: {
    header: {
      admin: {
        title: 'Ops Ekibi',
        subtitle:
          'Ops ekibi üyeleri Sipariş Yönetimi\'nde siparişlere atanabilir.',
      },
      seller: {
        title: 'Ekibim',
        subtitle:
          'Satıcı hesabına ekip üyesi ekle. Bu üyeler "Siparişlerim"\'de Ata seçeneği olarak görünür.',
      },
      supply: {
        title: 'Ekibim',
        subtitle:
          'Üretim ekibi üyeleri iş emirlerine atanabilir. Sahip + üretim lideri rol ayrımı korunur.',
      },
    },
    addMember: 'Üye Ekle',
    footerNote:
      'Bu çalışma alanının ekip üyeleri.',
    columns: {
      avatar: 'Avatar',
      name: 'Ad',
      email: 'E-posta',
      role: 'Rol',
      status: 'Durum',
      lastActive: 'Son aktif',
      actions: 'Eylemler',
    },
    status: {
      active: 'Aktif',
      invited: 'Davet edildi',
      inactive: 'Pasif',
    },
    invitedPrefix: 'Davet edildi',
    noLastActive: '—',
    states: {
      loading: 'Ekip yükleniyor…',
      loadFailed:
        'Ekip yüklenemedi. Tekrar deneyin.',
      empty: 'Henüz ekip üyesi yok. "Üye Ekle" ile başlayabilirsin.',
    },
    ariaLabels: {
      editMember: (name) => `${name} düzenle`,
      removeMember: (name) => `${name} kaldır`,
      cannotRemove: 'Owner/admin kaldırılamaz',
    },
    modal: {
      editTitle: 'Ekip Üyesini Düzenle',
      addTitle: 'Ekip Üyesi Ekle',
      descriptionEdit:
        'Ad, rol ve durumu güncelleyin.',
      descriptionAdd:
        'Ekip üyesi ekleyin.',
      displayName: 'Görünür Ad',
      displayNamePlaceholder: 'örn. Maya Patel',
      email: 'E-posta',
      emailPlaceholder: 'user@example.com',
      emailLocked:
        'E-posta düzenlenemez.',
      role: 'Rol',
      statusLabel: 'Durum',
      statusActive: 'Aktif',
      statusInvited: 'Davet edildi',
      statusInactive: 'Pasif',
      sendInviteLabel: 'Davet edildi olarak işaretle',
      cancel: 'Vazgeç',
      saving: 'Kaydediliyor…',
      update: 'Güncelle',
      sendInvite: 'Davet gönder',
      validation: {
        required: 'Görünür ad, e-posta ve rol zorunlu.',
        invalidEmail: 'Geçerli bir e-posta adresi gerekli.',
      },
    },
    deleteDialog: {
      title: 'Ekip üyesini kaldır?',
      leadBefore: '',
      leadAfter:
        ' ekipten çıkarılacak.',
      cancel: 'Vazgeç',
      confirm: 'Kaldır',
      forceConfirm: 'Yine de kaldır (force)',
      removing: 'Kaldırılıyor…',
    },
    rolesMatrix: {
      title: 'Roller — her rol ne yapabilir (salt-okunur önizleme)',
      footer:
        'Roller, her ekip üyesinin neler yapabileceğini açıklar.',
    },
    roles: {
      admin: {
        admin: {
          label: 'Admin',
          summary:
            'Tam platform yönetimi · Ring 3 denetim erişimi · ödemeleri onaylar',
        },
        ops: {
          label: 'Ops',
          summary:
            'Günlük sipariş akışı · atama + etiket · adres doğrulama incelemesi',
        },
        customer_support: {
          label: 'Müşteri Destek',
          summary:
            'Siparişleri okuma + müşteri iletişimi · değişim + bekletme yükseltme',
        },
        graphic_designer: {
          label: 'Grafik Tasarımcı',
          summary:
            'Kişiselleştirme kuyruğu · prova üretimi · tasarım varlık kütüphanesi',
        },
        shipping: {
          label: 'Kargo',
          summary: 'Toplu etiket basımı · kargo seçimi · alım planlama',
        },
        warehouse_lead: {
          label: 'Depo Lideri',
          summary:
            'Envanter havuzu · kabul + raflama · günlük giden parti lideri',
        },
      },
      seller: {
        owner: {
          label: 'Sahip',
          summary:
            'Hesap sahibi · faturalama + ekip konfigürasyonu · tam operasyonel kapsam',
        },
        manager: {
          label: 'Yönetici',
          summary:
            'Günlük sipariş + envanter + rapor gönderimi · ekip yönetemez',
        },
        assistant: {
          label: 'Asistan',
          summary:
            'Sipariş atama alıcısı · sınırlı düzenleme · sadece okuma faturalama',
        },
        designer: {
          label: 'Tasarımcı',
          summary:
            'Kişiselleştirme karşılama · prova teslimi · sipariş finansı yok',
        },
      },
      supply: {
        owner: {
          label: 'Sahip',
          summary:
            'Atölye sahibi · WO kabulü + fatura · tam ekip konfigürasyonu',
        },
        production_lead: {
          label: 'Üretim Lideri',
          summary:
            'Günlük WO üretim planlama · QC onayı · sevkiyat teslimi',
        },
        helper: {
          label: 'Yardımcı',
          summary:
            'Sadece atanan görevler · paketleme + son işlem · WO durumu değiştiremez',
        },
      },
    },
  },
}
